import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useRef, useState } from "react";
import { FaWhatsappSquare } from "react-icons/fa";
import AddressSearchComponent from "../AddressSearchComponent";
import { useForm } from "react-hook-form";
import Input from "@/app/components/ui/Inputs/Input";
import useFormValidations from "@/app/hooks/useFormValidations";
import clsx from "clsx";
import { useAddressSelectAPI } from "@/app/hooks/api_hooks/HDCart/useAddressSelectAPI";
import { ICartState, setAddressDetail, setAddressSelect } from "@/app_redux/reducers/slices/auth/cart_slice";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { IAddAddressBody } from "@/api_framework/api_modals/Users";
import { toast } from "react-hot-toast";
import { useAddressDetailAPI } from "@/app/hooks/api_hooks/HDCart/useAddressDetailAPI";
import { useAddressUpdateAPI } from "@/app/hooks/api_hooks/HDCart/useAddressUpdateAPI";
import { IHDCartAddressListResponseData, IHDCartAddressUpdatePutBody } from "@/api_framework/api_modals/Cart";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { useAddressListAPI } from "@/app/hooks/api_hooks/HDCart/useAddressListAPI";
import { useHDAddAddressAPI } from "@/app/hooks/api_hooks/HDCart/useHDAddAddressAPI";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { useWhatsappSendRequestAPI } from "@/app/hooks/api_hooks/HDCart/useWhatsappSendRequestAPI";
import OverbookingConfirmationModal from "@/app/components/routes/modals/OverbookingConfirmationModal";
import VMSConstants from "@/constants";

export interface IAddressSearchProps {
  addressSearch: string;
  address1: string;
  address2: string;
  receiverName: string;
  receiverNumber: string;
  pincode: string;
  city: string;
  state: string;
  address_space: "1" | "2" | "3";
}

const address_labels = [
  {
    label: "1",
    value: "Home",
  },
  {
    label: "2",
    value: "Office",
  },
  {
    label: "3",
    value: "Others",
  },
];

export default function AddAddressModal({
  onClose,
  addressId,
  onAddressAdded,
  currentAddress,
  setActiveModal,
  source = "homeService",
  closeOnOutsideClick = false,
  onOutsideClose,
  addAddressTitle,
  isAtHome = false,
}: {
  onClose: () => void;
  addressId?: string | null;
  onAddressAdded?: (newAddress: any) => void;
  currentAddress?: IHDCartAddressListResponseData | null;
  setActiveModal?: (modalName: "addressList" | "addAddress" | "addressClusterChange" | null) => void;
  source?: "cart" | "homeService";
  closeOnOutsideClick?: boolean;
  onOutsideClose?: () => void;
  addAddressTitle: string;
  isAtHome?: boolean;
}) {
  const { getRouteKey } = useRouter();
  const cancelButtonRef = useRef(null);
  const { forOnlyAlphabetWithAlfhanumeric, forMobile } = useFormValidations();
  const { addressSelect, addressDetail, activeCartDetails, activeCartId } = useRouteInfo(
    getRouteKey("CART_MANAGEMENT", "id"),
  ).routeState.state as ICartState;
  const fetchAddressSelect = useAddressSelectAPI();
  const { execute: addAddress } = useHDAddAddressAPI();
  const { routeState } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"));
  const { customerId, petId, customerDetails } = routeState.state as ICustomerProfileState;
  const { execute: fetchAddressDetail } = useAddressDetailAPI();
  const { execute: updateAddress } = useAddressUpdateAPI();
  const dispatch = useAppDispatch();
  const fetchAddressList = useAddressListAPI();
  const [_isSaving, setIsSaving] = useState(false);
  const [showRequestAddressSuccessModal, setShowRequestAddressSuccessModal] = useState(false);
  const { mappedClinicName, mappedClinicId } = useAppSelector((state) => state.root.UIStates);
  const userLogInfo = useAppSelector((state) => state.auth?.user?.email);
  const { execute: logAPI } = useClickLogAPI();
  const { execute: sendWhatsappRequest } = useWhatsappSendRequestAPI();
  const isCartSource = source === "cart";

  const normalizeReceiverNumber = (value: string): string => {
    const digitsOnly = (value ?? "").replace(/\D/g, "");
    if (digitsOnly.length === 12 && digitsOnly.startsWith("91")) return digitsOnly.slice(2);
    return digitsOnly;
  };

  const formHook = useForm<IAddressSearchProps>({
    mode: "onChange",
    defaultValues: {},
    shouldFocusError: true,
    criteriaMode: "all",
  });

  useEffect(() => {
    if (addressId) {
      fetchAddressDetail(addressId);
    }
  }, [addressId, formHook]);

  const callLogAPIBase = (
    item: string,
    eventType: "CART_ADD_ADDRESS" | "CART_EDIT_ADDRESS" | "CART_UPDATE_ADDRESS" | "CART_REQUEST_ADDRESS",
  ) => {
    const logBody: IClickLogBody = {
      event_type: eventType,
      scr_id: "CART",
      client_id: customerId,
      emr_id: activeCartDetails?.emr_id,
      cart_id: activeCartId,
      pet_id: petId,
      emr_cart_items: activeCartDetails,
      emr_cart_click_item: item,
      address_id: item,
      clinic_id: mappedClinicId,
      clinic_name: mappedClinicName,
      vms_user_id: userLogInfo,
      timestamp: Date.now(),
    };
    logAPI(logBody);
  };

  const callLogAPILabAddAddress = (item: any) => callLogAPIBase(item, "CART_ADD_ADDRESS");
  const callLogAPILabEditAddress = (item: any) => callLogAPIBase(item, "CART_EDIT_ADDRESS");
  const callLogAPILabUpdateAddress = (item: any) => callLogAPIBase(item, "CART_UPDATE_ADDRESS");
  const callLogAPILabRequestAddress = (item: any) => callLogAPIBase(item, "CART_REQUEST_ADDRESS");

  useEffect(() => {
    if (addressSelect) {
      formHook.setValue("address2", addressSelect.address_line_2 || "", { shouldValidate: true, shouldDirty: true });
      formHook.setValue("pincode", addressSelect.pincode || "", { shouldValidate: true, shouldDirty: true });
      formHook.setValue("city", addressSelect.city || "", { shouldValidate: true, shouldDirty: true });
      formHook.setValue("state", addressSelect.state || "", { shouldValidate: true, shouldDirty: true });
    }
  }, [addressSelect, formHook]);

  useEffect(() => {
    if (addressDetail) {
      const mapLabelToValue: Record<string, "1" | "2" | "3"> = {
        Home: "1",
        Office: "2",
        Others: "3",
      };
      formHook.reset({
        address1: addressDetail.address_1 || "",
        address2: addressDetail.address_2 || "",
        receiverName: isCartSource ? addressDetail.receiver_name || "" : "",
        receiverNumber: isCartSource ? addressDetail.receiver_number || "" : "",
        pincode: addressDetail.pincode || "",
        city: addressDetail.city || "",
        state: addressDetail.state || "",
        address_space: mapLabelToValue[addressDetail.label] || undefined,
      });
    }
  }, [addressDetail, formHook, isCartSource]);

  useEffect(() => {
    if (addressId && currentAddress) {
      callLogAPILabEditAddress("Edit Address Modal Opened");
    }
  }, [addressId]);

  const handleSubmit = async (data: IAddressSearchProps) => {
    if (!data.address_space) {
      toast.error("Please select a label");
      return;
    }
    const selectedLabel = address_labels.find((lbl) => lbl.label === data.address_space)?.value || "";
    const latitudeNum = addressSelect?.location?.lat ? Number(addressSelect.location.lat) : undefined;
    const longitudeNum = addressSelect?.location?.lng ? Number(addressSelect.location.lng) : undefined;
    const normalizedReceiverNumber = normalizeReceiverNumber(data?.receiverNumber);
    if (isCartSource && normalizedReceiverNumber && normalizedReceiverNumber.length !== 10) {
      formHook.setError("receiverNumber", { type: "validate", message: "Enter a valid 10-digit number" });
      toast.error("Please enter a valid receiver number");
      return;
    }
    const constructedBody: IAddAddressBody = {
      user_id: customerId,
      address_1: data?.address1,
      address_2: data?.address2,
      receiver_name: isCartSource ? data?.receiverName : undefined,
      receiver_number: isCartSource ? normalizedReceiverNumber : undefined,
      city: data?.city,
      pincode: data?.pincode,
      state: data?.state,
      label: selectedLabel,
      latitude: latitudeNum?.toString() ?? "",
      longitude: longitudeNum?.toString() ?? "",
      old_address_id: currentAddress?.address_id,
      clinic_id: mappedClinicId,
       ...(isAtHome ? { source: "at_home" } : {}),
    };
    const constructedUpdateBody: IHDCartAddressUpdatePutBody = {
      user_id: customerId,
      address_1: data?.address1,
      address_2: data?.address2,
      receiver_name: isCartSource ? data?.receiverName : undefined,
      receiver_number: isCartSource ? normalizedReceiverNumber : undefined,
      city: data?.city,
      pincode: data?.pincode,
      state: data?.state,
      label: selectedLabel,
      latitude: latitudeNum?.toString() ?? "",
      longitude: longitudeNum?.toString() ?? "",
      ...(isAtHome ? { source: "at_home" } : {}),
    };
    try {
      setIsSaving(true);
      if (addressDetail?.address_id) {
        await updateAddress(addressDetail?.address_id, constructedUpdateBody);
        callLogAPILabUpdateAddress(addressDetail?.address_id);
        const updatedList = await fetchAddressList(customerId, null, null, mappedClinicId);
        const updatedAddress = updatedList?.find((a) => a.address_id === addressDetail?.address_id);
        onAddressAdded?.(updatedAddress);
        onClose();
      } else {
        const response = await addAddress(constructedBody);
        if (!response) {
          // toast.error("Failed to create address");
          return;
        }
        callLogAPILabAddAddress(addressDetail?.address_id);
        const updatedList = await fetchAddressList(customerId, null, null, mappedClinicId);
        const newAddress = updatedList?.find((a) => a.address_id === response.generated_address_id);
        if (response.show_screen === true) {
          onAddressAdded?.(newAddress);
          setActiveModal?.("addressClusterChange");
          return; // stop here
        }

        // ✅ Otherwise normal behavior
        onAddressAdded?.(newAddress);
        onClose();
      }
    } catch (error) {
      console.error("Error while saving address:", error);
      // toast.error("Failed to save address");
    } finally {
      setIsSaving(false);
    }
  };
  const handelClose = () => {
    formHook.reset({
      addressSearch: "",
      address1: "",
      address2: "",
      receiverName: "",
      receiverNumber: "",
      pincode: "",
      city: "",
      state: "",
      address_space: undefined,
    });
    dispatch(setAddressDetail({ data: null }));
    dispatch(setAddressSelect({ data: null }));
    onClose();
  };

  return (
    <div>
      <Transition.Root show={true} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-40"
          initialFocus={cancelButtonRef}
          onClose={closeOnOutsideClick ? (onOutsideClose ?? handelClose) : () => { }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel
                  className={`relative transform overflow-visible rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-4 sm:max-w-2xl sm:w-full sm:p-6`}
                >
                  <div className="flex justify-between items-center">
                    <div className="font-semibold text-sm">{addAddressTitle}</div>
                    <div className="flex gap-5">
                      <div
                        className="flex gap-2 bg-[#f5f5f5] items-center rounded-md p-2 cursor-pointer"
                        onClick={async () => {
                          const payload = {
                            send_to: customerDetails?.mobile,
                            template_name: isAtHome ? "request_address_at_home_services" : "request_address_hd_order",
                            variables: {
                              // AHS template already embeds the address URL; pass only the user_id and flow tag.
                              1: isAtHome
                                ? `${customerId}&flow=at_home`
                                : `https://address.vetic.in/?user_id=${customerId}&flow=hd_order`
                            },
                          };
                          const response = await sendWhatsappRequest(payload);
                          callLogAPILabRequestAddress(addressDetail?.address_id);
                          if (response?.status) {
                            setShowRequestAddressSuccessModal(true);
                          }
                        }}
                      >
                        <FaWhatsappSquare color="green" size={22} />
                        <div className="text-[rgba(0,104,255,1)] text-sm font-semibold">Request Address</div>
                      </div>
                      <button
                        type="button"
                        onClick={handelClose}
                        className="text-gray-400 hover:text-gray-600 focus:outline-none"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                  <form onSubmit={formHook.handleSubmit(handleSubmit)}>
                    <div className="col-span-2 w-full mt-5">
                      <AddressSearchComponent
                        autoComplete="off"
                        autoCorrect="false"
                        showDropdownWhenNoData
                        withClearAction
                        onClear={() => {
                          formHook.setValue("address2", "", { shouldValidate: true, shouldDirty: true });
                          formHook.setValue("pincode", "", { shouldValidate: true, shouldDirty: true });
                          formHook.setValue("city", "", { shouldValidate: true, shouldDirty: true });
                          formHook.setValue("state", "", { shouldValidate: true, shouldDirty: true });
                        }}
                        onSelectItem={(item) => {
                          if (item?.state?.placeId) {
                            fetchAddressSelect(item.state.placeId);
                          }
                          formHook?.clearErrors("address2");
                        }}
                        errorMessages={[{ message: "Address 2 is required", type: "required" }]}
                        fieldError={formHook?.formState?.errors?.addressSearch}
                        register={formHook.register("addressSearch", {
                          required: false,
                        })}
                        showTypedErrors={false}
                        placeholder="Search Google Map"
                        className={""}
                        itemsClassName={""}
                        listBoxClassName={""}
                        showTooltips={""}
                      />
                    </div>
                    <div className="mt-5">
                      <Input
                        className="text-xs"
                        placeholder="Flat, Building, Landmark"
                        register={formHook.register("address1", {
                          required: true,
                          ...forOnlyAlphabetWithAlfhanumeric.validations,
                        })}
                        fieldError={formHook.formState.errors.address1}
                        errorMessages={[forOnlyAlphabetWithAlfhanumeric.errors, { message: null, type: "required" }]}
                      />
                    </div>
                    <div className="mt-3">
                      <Input
                        className="text-xs"
                        placeholder="Address line 2"
                        register={formHook.register("address2", {
                          required: true,
                          ...forOnlyAlphabetWithAlfhanumeric.validations,
                        })}
                        fieldError={formHook.formState.errors.address2}
                        errorMessages={[forOnlyAlphabetWithAlfhanumeric.errors, { message: null, type: "required" }]}
                        disabled={!!addressSelect?.address_line_2}
                      />
                    </div>
                    {isCartSource && (
                      <div className="flex gap-5 mt-3">
                        <div className="w-full">
                          <Input
                            className="text-xs"
                            placeholder="Receiver name"
                            register={formHook.register("receiverName", {
                              required: false,
                              validate: (value) => {
                                const receiverName = String(value ?? "").trim();
                                if (!receiverName) return true;
                                return /^[a-zA-Z\s]+$/.test(receiverName) ? true : "Only alphabets are allowed";
                              },
                            })}
                            fieldError={formHook.formState.errors.receiverName}
                            errorMessages={[{ message: "Only alphabets are allowed", type: "validate" }]}
                          />
                        </div>
                        <div className="w-full">
                          <Input
                            className="text-xs"
                            placeholder="Receiver number"
                            type="tel"
                            inputMode="numeric"
                            maxLength={10}
                            register={formHook.register("receiverNumber", {
                              required: false,
                              setValueAs: (value) => normalizeReceiverNumber(String(value ?? "")),
                              onChange: (e) => {
                                // Keep the rendered input digits-only (and trim leading +91 if pasted)
                                e.target.value = normalizeReceiverNumber(e.target.value);
                              },
                              validate: (value) => {
                                const receiverNumber = String(value ?? "");
                                if (!receiverNumber) return true;
                                return receiverNumber.length === 10 ? true : "Enter a valid 10-digit number";
                              },
                            })}
                            fieldError={formHook.formState.errors.receiverNumber}
                            errorMessages={[{ type: "validate", message: "Enter a valid 10-digit number" }]}
                          />
                        </div>
                      </div>
                    )}
                    <div className="flex gap-5">
                      <div className="mt-3">
                        <Input
                          className="text-xs"
                          placeholder="Pincode"
                          disabled={!!addressSelect?.pincode}
                          register={formHook.register("pincode", {
                            required: !isAtHome,
                            ...forOnlyAlphabetWithAlfhanumeric.validations,
                          })}
                          fieldError={formHook.formState.errors.pincode}
                          errorMessages={[forOnlyAlphabetWithAlfhanumeric.errors, { message: null, type: "required" }]}
                        />
                      </div>
                      <div className="mt-3">
                        <Input
                          className="text-xs"
                          placeholder="City"
                          register={formHook.register("city", {
                            required: true,
                            ...forOnlyAlphabetWithAlfhanumeric.validations,
                          })}
                          fieldError={formHook.formState.errors.city}
                          errorMessages={[forOnlyAlphabetWithAlfhanumeric.errors, { message: null, type: "required" }]}
                          disabled={!!addressSelect?.city}
                        />
                      </div>
                      <div className="mt-3">
                        <Input
                          className="text-xs"
                          placeholder="State"
                          register={formHook.register("state", {
                            required: true,
                            ...forOnlyAlphabetWithAlfhanumeric.validations,
                          })}
                          fieldError={formHook.formState.errors.state}
                          errorMessages={[forOnlyAlphabetWithAlfhanumeric.errors, { message: null, type: "required" }]}
                          disabled={!!addressSelect?.state}
                        />
                      </div>
                    </div>
                    <div className="flex gap-5 mt-3">
                      {address_labels.length > 0 &&
                        address_labels.map((adL, idx) => {
                          return (
                            <div key={`${adL.label}:${idx}`} className="">
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  formHook.setValue("address_space", adL.label as "1" | "2" | "3");
                                }}
                                className={clsx(
                                  "border opacity-100 w-full py-1 px-3 rounded-2xl text-sm",
                                  formHook.watch("address_space") === adL.label
                                    ? "border-[#04853c] text-[#04853c] opacity-100 focus:border-[#04853c] hover:bg-[rgba(128,141,160,0.1)] hover:border-gray-200 bg-[#f7fffb]"
                                    : "text-gray-400 border-gray-300 focus:border-[#193a6a] hover:bg-[rgba(128,141,160,0.1)] hover:border-gray-200",
                                )}
                                id="address_home"
                              >
                                {adL.value}
                              </button>
                            </div>
                          );
                        })}
                    </div>
                    <div className="mt-5">
                      <button
                        type="submit"
                        disabled={!formHook.formState.isValid}
                        className={clsx(
                          "rounded-md w-full px-10 py-[9px] font-normal text-sm shadow-sm",
                          formHook.formState.isValid
                            ? "bg-black text-white cursor-pointer"
                            : "bg-gray-300 text-gray-500 cursor-not-allowed",
                        )}
                      >
                        Save Address
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      <OverbookingConfirmationModal
        open={showRequestAddressSuccessModal}
        title="Address requested on WhatsApp"
        message={VMSConstants.string.whatsappAddressRequestSuccessMessage}
        cancelText="OK"
        onClose={() => setShowRequestAddressSuccessModal(false)}
        onConfirm={() => setShowRequestAddressSuccessModal(false)}
        confirmText=""
        confirmButtonClassName="hidden"
      />
    </div>
  );
}
